/**
 * Compat-Axelia de los grabadores: la codificación que cada NVR guarda para
 * cada cámara, contra la misma plantilla que la vista de Cámaras.
 *
 * Por qué vive aquí y no en Cámaras: el grabador **se la reimpone** a la
 * cámara. El 28-oct-2026 las cámaras planchadas volvían a los valores del NVR
 * en 2-8 minutos; plancharlas otra vez no servía de nada. Se plancha el canal
 * del grabador, y la escritura llega también a la cámara.
 *
 * Sólo la plantilla: grabación, disco, hora y red son la otra vista.
 */
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ApiError } from "@/api/client";
import {
  useGrabadoresAxelia,
  usePlancharCanalGrabador,
  useTakeSnapshot,
} from "@/hooks/useConfig";
import type { GrabadorCanalAxelia } from "@/api/types";
import { Fragment, useState } from "react";

export function GrabadoresAxeliaPanel() {
  const { data, isLoading, isError } = useGrabadoresAxelia();
  const [abiertas, setAbiertas] = useState<Set<string>>(new Set());
  const alternar = (nombre: string) =>
    setAbiertas((previas) => {
      const copia = new Set(previas);
      if (copia.has(nombre)) copia.delete(nombre);
      else copia.add(nombre);
      return copia;
    });
  const plancha = usePlancharCanalGrabador();
  const [confirmando, setConfirmando] = useState<string | null>(null);
  const [resultado, setResultado] = useState<Record<string, string>>({});
  const cosecha = useTakeSnapshot();
  // Agencia que se está planchando completa, y por dónde va. Una a la vez.
  const [enCurso, setEnCurso] = useState<{
    agencia: string;
    texto: string;
  } | null>(null);

  // Devuelve la promesa para que "Planchar agencia" pueda ir canal por canal.
  const planchar = async (deviceId: string, canal: string) => {
    const clave = `${deviceId}/${canal}`;
    setConfirmando(null);
    setResultado((r) => ({ ...r, [clave]: "planchando…" }));
    let texto: string;
    try {
      const res = await plancha.mutateAsync({ deviceId, canal });
      texto = res.aplicado
        ? "✔ planchado"
        : `no se planchó: ${res.motivo ?? "revisar"}`;
    } catch (e) {
      texto = e instanceof ApiError ? e.message : "no se pudo planchar";
    }
    setResultado((r) => ({ ...r, [clave]: texto }));
  };

  // Los canales fuera de la agencia, EN SERIE: el 28-oct tres planchados a la
  // vez contra el mismo grabador dejaron canales a medias. Al final se
  // recosechan sus grabadores para que la tabla muestre lo que quedó.
  const plancharAgencia = async (
    agencia: string,
    fuera: GrabadorCanalAxelia[],
  ) => {
    setConfirmando(null);
    for (const [i, c] of fuera.entries()) {
      setEnCurso({
        agencia,
        texto: `planchando ${i + 1} de ${fuera.length} (D${c.canal})…`,
      });
      await planchar(c.device_id, c.canal);
    }
    const grabadores = [...new Set(fuera.map((c) => c.device_id))];
    for (const [i, id] of grabadores.entries()) {
      setEnCurso({
        agencia,
        texto: `cosechando grabador ${i + 1} de ${grabadores.length}…`,
      });
      try {
        await cosecha.mutateAsync(id);
      } catch {
        // Si la cosecha falla el planchado ya quedó; la tabla se queda vieja
        // hasta la siguiente cosecha, y eso se dice abajo.
      }
    }
    setEnCurso({ agencia, texto: "✔ terminado: revisa la tabla" });
  };

  if (isLoading)
    return <p className="text-sm text-muted-foreground">Cargando canales…</p>;
  if (isError || !data)
    return (
      <p className="text-sm text-destructive">
        No se pudo consultar los canales de los grabadores.
      </p>
    );
  if (data._status === "sin_configurar")
    return <p className="text-sm text-muted-foreground">{data.mensaje}</p>;

  const t = data.totales;
  return (
    <div className="space-y-4">
      <p className="text-sm">
        <strong>{t?.cumplen ?? 0}</strong> de <strong>{t?.canales ?? 0}</strong>{" "}
        canales cumplen la plantilla ·{" "}
        <span className="text-destructive">{t?.fuera ?? 0} fuera</span> ·{" "}
        <span className="text-muted-foreground">
          {t?.grabadores_sin_leer ?? 0} grabadores sin leer
        </span>
      </p>
      <p className="text-xs text-muted-foreground">
        Es la codificación que el grabador <strong>guarda y le reimpone</strong>{" "}
        a cada cámara: si aquí está fuera, la cámara vuelve a estar fuera por
        mucho que se planche. Misma plantilla que Cámaras: principal H.265
        (H.264 si el modelo no lo tiene), 1280x720, VBR, calidad media, 6 fps,
        tope 256 kbps; secundario H.264. El I-frame no: lo maneja la cámara, no
        el grabador. Los paneles de acceso grabados en un canal no son cámaras y
        no se cuentan
        {t?.paneles_omitidos ? ` (${t.paneles_omitidos} omitidos)` : ""}.
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Agencia</TableHead>
            <TableHead className="text-right">Cumplen</TableHead>
            <TableHead className="text-right">Fuera</TableHead>
            <TableHead className="text-right">Grabadores sin leer</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(data.agencias ?? []).map((a) => {
            const abierta = abiertas.has(a.nombre);
            const suyos = (data.canales ?? []).filter(
              (c) => c.agencia === a.nombre,
            );
            const sinLeer = (data.sin_leer ?? []).filter(
              (g) => g.agencia === a.nombre,
            );
            const fuera = suyos.filter((c) => !c.cumple);
            const claveAgencia = `agencia/${a.nombre}`;
            const ocupado = enCurso !== null && !enCurso.texto.startsWith("✔");
            return (
              <Fragment key={a.nombre}>
                <TableRow
                  onClick={() => alternar(a.nombre)}
                  className="cursor-pointer"
                  aria-expanded={abierta}
                >
                  <TableCell className="font-medium">
                    <span className="mr-1 inline-block w-3 text-muted-foreground">
                      {abierta ? "▾" : "▸"}
                    </span>
                    {a.nombre}
                  </TableCell>
                  <TableCell className="text-right">{a.cumplen}</TableCell>
                  <TableCell
                    className={
                      a.fuera > 0
                        ? "text-right font-medium text-destructive"
                        : "text-right"
                    }
                  >
                    {a.fuera}
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {a.sin_leer}
                  </TableCell>
                </TableRow>
                {abierta && (
                  <TableRow>
                    <TableCell colSpan={4} className="bg-muted/40 p-0">
                      {(fuera.length > 0 || enCurso?.agencia === a.nombre) && (
                        <div className="flex items-center gap-2 border-b px-3 py-2 text-xs">
                          {enCurso?.agencia === a.nombre ? (
                            <span>{enCurso.texto}</span>
                          ) : confirmando === claveAgencia ? (
                            <span>
                              ¿Escribir {fuera.length} canales, uno por uno?{" "}
                              <button
                                type="button"
                                className="font-medium text-destructive underline"
                                onClick={() =>
                                  void plancharAgencia(a.nombre, fuera)
                                }
                              >
                                Sí, planchar agencia
                              </button>{" "}
                              <button
                                type="button"
                                className="underline"
                                onClick={() => setConfirmando(null)}
                              >
                                No
                              </button>
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="rounded border px-2 py-0.5 font-medium disabled:opacity-50"
                              disabled={ocupado}
                              onClick={() => setConfirmando(claveAgencia)}
                            >
                              Planchar agencia ({fuera.length})
                            </button>
                          )}
                        </div>
                      )}
                      <table className="w-full text-xs">
                        <thead className="text-muted-foreground">
                          <tr className="border-b">
                            <th className="px-3 py-1.5 text-left font-medium">
                              Grabador
                            </th>
                            <th className="px-3 py-1.5 text-left font-medium">
                              Canal
                            </th>
                            <th className="px-3 py-1.5 text-left font-medium">
                              Cámara
                            </th>
                            <th className="px-3 py-1.5 text-left font-medium">
                              Modelo
                            </th>
                            <th className="px-3 py-1.5 text-left font-medium">
                              Detalle
                            </th>
                            <th className="px-3 py-1.5 text-left font-medium" />
                          </tr>
                        </thead>
                        <tbody>
                          {suyos.map((c) => {
                            const clave = `${c.device_id}/${c.canal}`;
                            return (
                              <tr
                                key={clave}
                                className="border-b last:border-0"
                              >
                                <td className="px-3 py-1.5 text-muted-foreground">
                                  {c.grabador_ip}
                                </td>
                                <td className="px-3 py-1.5 font-medium">
                                  D{c.canal}
                                </td>
                                <td className="px-3 py-1.5">{c.ip ?? "—"}</td>
                                <td className="px-3 py-1.5 text-muted-foreground">
                                  {c.modelo ?? "—"}
                                </td>
                                <td className="px-3 py-1.5">
                                  {c.cumple ? (
                                    <span
                                      className="text-muted-foreground"
                                      title={
                                        c.oro_propio?.length
                                          ? `Oro propio de este canal: ${c.oro_propio.join(", ")}`
                                          : undefined
                                      }
                                    >
                                      {c.oro_propio?.length ? "✔ cumple (oro propio)" : "✔ cumple"}
                                    </span>
                                  ) : (
                                    c.desvios.map((d) => (
                                      <div key={d.campo}>
                                        {d.campo}:{" "}
                                        <code>{String(d.actual)}</code> →{" "}
                                        <code>{d.esperado}</code>
                                      </div>
                                    ))
                                  )}
                                </td>
                                <td className="whitespace-nowrap px-3 py-1.5">
                                  {resultado[clave] ? (
                                    <span>{resultado[clave]}</span>
                                  ) : c.cumple ? null : confirmando ===
                                    clave ? (
                                    <span>
                                      ¿Escribir en el grabador?{" "}
                                      <button
                                        type="button"
                                        className="font-medium text-destructive underline"
                                        onClick={() =>
                                          void planchar(c.device_id, c.canal)
                                        }
                                      >
                                        Sí, planchar
                                      </button>{" "}
                                      <button
                                        type="button"
                                        className="underline"
                                        onClick={() => setConfirmando(null)}
                                      >
                                        No
                                      </button>
                                    </span>
                                  ) : (
                                    <button
                                      type="button"
                                      className="rounded border px-2 py-0.5 font-medium disabled:opacity-50"
                                      disabled={ocupado}
                                      onClick={() => setConfirmando(clave)}
                                    >
                                      Planchar
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                          {sinLeer.map((g) => (
                            <tr
                              key={g.device_id}
                              className="border-b opacity-70 last:border-0"
                            >
                              <td className="px-3 py-1.5 text-muted-foreground">
                                {g.ip}
                              </td>
                              <td
                                colSpan={4}
                                className="px-3 py-1.5 text-muted-foreground"
                              >
                                sin leer — {g.motivo}
                              </td>
                              <td />
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
